import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import { adminApi, publicApi } from '../../utils/api';
import { InfoTag } from '../../components/ui/InfoTag';

const shellStyle = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border-subtle)',
};

const sectionMotion = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35 },
};

const card = 'terminal-grid p-6';

const splitCsv = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

const statusTone = {
  configured: { color: '#0b0', bg: 'rgba(0,180,0,0.12)' },
  missing: { color: '#ff8a00', bg: 'rgba(255,138,0,0.12)' },
  active: { color: '#0b0', bg: 'rgba(0,180,0,0.12)' },
  draft: { color: '#7dd3fc', bg: 'rgba(125,211,252,0.12)' },
  queued: { color: '#facc15', bg: 'rgba(250,204,21,0.12)' },
  published: { color: '#0b0', bg: 'rgba(0,180,0,0.12)' },
  paused: { color: '#ff8a00', bg: 'rgba(255,138,0,0.12)' },
};

const integrationNames = ['Buffer', 'ConvertKit', 'Resend', 'Make Webhook', 'Google OAuth'];

const toLowerText = (value) => String(value || '').toLowerCase();

const looksLikeDevelopmentIssue = (event) => {
  const text = [event?.component, event?.event_type, event?.message].map(toLowerText).join(' ');
  return ['dev', 'debug', 'build', 'deploy', 'release', 'test', 'lint', 'migration', 'schema'].some((keyword) => text.includes(keyword));
};

const looksLikeApiIssue = (event) => {
  const text = [event?.component, event?.event_type, event?.message].map(toLowerText).join(' ');
  return ['api', 'auth', 'oauth', 'token', 'webhook', 'resend', 'convertkit', 'buffer', 'grok', 'openai', 'anthropic', 'gemini'].some((keyword) => text.includes(keyword));
};

const looksLikeEnvironmentIssue = (event) => {
  const text = [event?.component, event?.event_type, event?.message].map(toLowerText).join(' ');
  return ['permission', 'access', 'cors', 'env', 'environment', 'connection', 'dns', 'certificate', 'refused', 'timeout'].some((keyword) => text.includes(keyword));
};

const buildFailureFingerprint = (event) => {
  const component = toLowerText(event?.component || 'unknown');
  const eventType = toLowerText(event?.event_type || 'event');
  const message = toLowerText(event?.message || '').replace(/\s+/g, ' ').trim();
  return `${component}|${eventType}|${message}`;
};

const getFailureSolutionHint = (event) => {
  const text = [event?.component, event?.event_type, event?.message, event?.fallback_status].map(toLowerText).join(' ');
  if (text.includes('/api/admin/ops/config/diagnostics') || text.includes('ops/config/diagnostics')) {
    return 'Check backend/routes/admin_growth.py get_ops_config_diagnostics and integration probes (Buffer/ConvertKit). Validate BUFFER_ACCESS_TOKEN and CONVERTKIT_* keys in backend/.env; if probes are healthy but still slow, cache diagnostics results or reduce per-provider timeout.';
  }
  if (text.includes('cors') || text.includes('origin')) {
    return 'Check backend/server.py CORS_ORIGINS and frontend/src/utils/api.js base URL alignment.';
  }
  if (text.includes('token') || text.includes('auth') || text.includes('jwt')) {
    return 'Check backend/auth.py token validation and admin auth headers in frontend/src/utils/api.js.';
  }
  if (text.includes('resend') || text.includes('newsletter')) {
    return 'Check backend/routes/newsletter.py plus RESEND_* keys in backend/.env.';
  }
  if (text.includes('mongo') || text.includes('database') || text.includes('duplicate key')) {
    return 'Check backend/database.py connection settings and backend/routes/admin.py write paths.';
  }
  if (text.includes('timeout') || text.includes('connection refused') || text.includes('dns')) {
    return 'Check backend/routes/admin_growth.py health checks and infra/network env configuration.';
  }
  if (text.includes('deprecated') || text.includes('sourcemap') || text.includes('dev server')) {
    return 'Low-value development warning; check backend/services/system_diagnostics.py noise filters.';
  }
  return 'Check backend/routes/admin_growth.py system events and the referenced component handler.';
};

const TextInput = ({ label, value, onChange, placeholder = '', type = 'text' }) => (
  <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
    {label}
    <input
      type={type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="input-pro mt-2"
    />
  </label>
);

const TextArea = ({ label, value, onChange, rows = 4, placeholder = '' }) => (
  <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
    {label}
    <textarea
      value={value}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="input-pro mt-2"
    />
  </label>
);

const SelectInput = ({ label, value, onChange, options }) => (
  <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
    {label}
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="input-pro mt-2"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

const CheckboxInput = ({ label, checked, onChange }) => (
  <label className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
    <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
    {label}
  </label>
);

const StatusPill = ({ status }) => {
  const tone = statusTone[status] || { color: 'var(--text-muted)', bg: 'rgba(255,255,255,0.06)' };
  return (
    <span className="px-2 py-1 text-xs uppercase font-mono" style={{ color: tone.color, background: tone.bg, border: '1px solid var(--border-subtle)' }}>
      {status || 'unknown'}
    </span>
  );
};

const SaveBar = ({ saving, onSave, label = 'Save' }) => (
  <button onClick={onSave} disabled={saving} className="btn-primary">
    {saving ? 'Saving...' : label}
  </button>
);

export const NewsletterAdminTab = () => {
  // Default newsletter template blocks
  const DEFAULT_HEADER_HTML = `<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 0; background-color: #f5f5f5; }
    .container { max-width: 600px; margin: 0 auto; background-color: #fff; }
    .header { background: linear-gradient(135deg, #0B1020 0%, #1A2333 100%); color: #FFF; padding: 30px 20px; text-align: center; }
    .header h1 { margin: 0 0 10px 0; font-size: 24px; }
    .header p { margin: 0; font-size: 14px; opacity: 0.9; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>Wowrolla Newsletter</h1>
      <p>Your weekly AI tools digest</p>
    </div>`;

  const DEFAULT_FOOTER_HTML = `    <div style="border-top: 1px solid #eee; padding: 20px; text-align: center; font-size: 12px; color: #666;">
      <p style="margin: 10px 0;"><a href="[unsubscribe_link]" style="color: #0B1020; text-decoration: none;">Unsubscribe</a></p>
      <p style="margin: 5px 0;">© 2024 Wowrolla. All rights reserved.</p>
      <p style="margin: 5px 0;">Made with ❤️ by the Wowrolla team</p>
    </div>
  </div>
</body>
</html>`;

  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingCampaign, setSavingCampaign] = useState(false);
  const [settings, setSettings] = useState({ double_opt_in: true, default_audience_tag: 'all', auto_sync_convertkit: true, enable_weekly_digest: true, default_sender_name: 'Wowrolla' });
  const [dashboard, setDashboard] = useState({ summary: {}, top_sources: [], campaigns: [] });
  const [campaignForm, setCampaignForm] = useState({ 
    id: '', 
    title: '', 
    subject: '', 
    preview_text: '', 
    audience_tag: 'all', 
    status: 'draft', 
    content: '',
    header_html: DEFAULT_HEADER_HTML,
    content_html: '',
    footer_html: DEFAULT_FOOTER_HTML,
    cta_label: 'Read more', 
    cta_url: '/newsletter',
    scheduled_for: '' 
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const dashboardRes = await adminApi.getNewsletterDashboard();
      setDashboard(dashboardRes.data || { summary: {}, top_sources: [], campaigns: [] });
      setSettings((dashboardRes.data || {}).settings || { double_opt_in: true, default_audience_tag: 'all', auto_sync_convertkit: true, enable_weekly_digest: true, default_sender_name: 'Wowrolla' });
    } catch (error) {
      alert('Failed to load newsletter admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!campaignForm.id && !campaignForm.audience_tag) {
      setCampaignForm((prev) => ({ ...prev, audience_tag: settings.default_audience_tag || 'all' }));
    }
  }, [settings.default_audience_tag, campaignForm.id, campaignForm.audience_tag]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await adminApi.updateNewsletterSettings(settings);
      await load();
    } catch {
      alert('Failed to save newsletter settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const resetCampaignForm = () => {
    setCampaignForm({ 
      id: '', 
      title: '', 
      subject: '', 
      preview_text: '', 
      audience_tag: settings.default_audience_tag || 'all', 
      status: 'draft', 
      content: '', 
      header_html: DEFAULT_HEADER_HTML,
      content_html: '',
      footer_html: DEFAULT_FOOTER_HTML,
      cta_label: 'Read more', 
      cta_url: '/newsletter', 
      scheduled_for: '' 
    });
  };

  const coerceString = (value) => String(value ?? '');

  const isValidHtmlInput = (htmlInput) => {
    const raw = coerceString(htmlInput).trim();
    if (!raw) {
      return { valid: false, reason: 'Newsletter content HTML is required.' };
    }
    const parser = new DOMParser();
    const parsed = parser.parseFromString(raw, 'text/html');
    const parserErrors = parsed.querySelector('parsererror');
    if (parserErrors) {
      return { valid: false, reason: 'The pasted HTML is not valid. Fix malformed tags and try again.' };
    }
    const hasTagLikeMarkup = /<\s*[a-zA-Z][^>]*>/.test(raw);
    if (!hasTagLikeMarkup) {
      return { valid: false, reason: 'Please paste HTML markup (for example <div>...</div>) instead of plain text.' };
    }
    return { valid: true, reason: '' };
  };

  const contentHtmlValidation = isValidHtmlInput(campaignForm.content_html);
  const compiledNewsletterHtml = `${coerceString(campaignForm.header_html)}${coerceString(campaignForm.content_html)}${coerceString(campaignForm.footer_html)}`;

  const editCampaign = (campaign) => {
    setCampaignForm({
      id: coerceString(campaign.id || ''),
      title: coerceString(campaign.title || ''),
      subject: coerceString(campaign.subject || ''),
      preview_text: coerceString(campaign.preview_text || ''),
      audience_tag: coerceString(campaign.audience_tag || settings.default_audience_tag || 'all'),
      status: coerceString(campaign.status || 'draft'),
      content: coerceString(campaign.content || ''),
      header_html: coerceString(campaign.header_html || DEFAULT_HEADER_HTML),
      content_html: coerceString(campaign.content_html || campaign.content || ''),
      footer_html: coerceString(campaign.footer_html || DEFAULT_FOOTER_HTML),
      cta_label: coerceString(campaign.cta_label || 'Read more'),
      cta_url: coerceString(campaign.cta_url || '/newsletter'),
      scheduled_for: coerceString(campaign.scheduled_for || ''),
    });
  };

  const saveCampaign = async () => {
    if (!campaignForm.title || !campaignForm.subject) {
      alert('Campaign title and subject are required');
      return;
    }

    const htmlValidation = isValidHtmlInput(campaignForm.content_html);
    if (!htmlValidation.valid) {
      alert(htmlValidation.reason);
      return;
    }

    setSavingCampaign(true);
    try {
      const sanitized = {
        ...campaignForm,
        id: coerceString(campaignForm.id).trim(),
        title: coerceString(campaignForm.title).trim(),
        subject: coerceString(campaignForm.subject).trim(),
        preview_text: coerceString(campaignForm.preview_text).trim(),
        audience_tag: coerceString(campaignForm.audience_tag).trim() || 'all',
        status: coerceString(campaignForm.status).trim() || 'draft',
        header_html: coerceString(campaignForm.header_html).trim(),
        content_html: coerceString(campaignForm.content_html).trim(),
        footer_html: coerceString(campaignForm.footer_html).trim(),
        cta_label: coerceString(campaignForm.cta_label).trim() || 'Read more',
        cta_url: coerceString(campaignForm.cta_url).trim() || '/newsletter',
        scheduled_for: coerceString(campaignForm.scheduled_for).trim(),
      };
      sanitized.content = `${sanitized.header_html}${sanitized.content_html}${sanitized.footer_html}`.trim();
      await adminApi.saveNewsletterCampaign(sanitized);
      await load();
      resetCampaignForm();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to save campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const automateCampaign = async (campaignId, action) => {
    setSavingCampaign(true);
    try {
      const result = await adminApi.automateNewsletterCampaign(campaignId, {
        action,
        scheduled_for: campaignForm.scheduled_for || null,
      });
      const actionLabel = action === 'publish_now' ? 'published' : 'scheduled';
      const queueId = result?.data?.queue_id || result?.queue_id;
      const status = result?.data?.status || result?.status || 'processing';

      alert(`Campaign ${actionLabel}. Status: ${status}${queueId ? `\nQueue ID: ${queueId}` : ''}`);
      await load();
      resetCampaignForm();
    } catch (error) {
      alert(error?.response?.data?.detail || error?.message || 'Failed to automate campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign?')) return;
    setSavingCampaign(true);
    try {
      await adminApi.deleteNewsletterCampaign(campaignId);
      if (campaignForm.id === campaignId) {
        resetCampaignForm();
      }
      await load();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete campaign');
    } finally {
      setSavingCampaign(false);
    }
  };

  if (loading) {
    return <div className={card}><p style={{ color: 'var(--text-secondary)' }}>Loading newsletter admin...</p></div>;
  }

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="newsletter-admin-tab">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard label="Subscribers" value={dashboard.summary?.subscribers || 0} />
        <MetricCard label="Confirmed" value={dashboard.summary?.confirmed || 0} />
        <MetricCard label="Active" value={dashboard.summary?.active || 0} />
        <MetricCard label="Confirm Rate" value={`${dashboard.summary?.confirm_rate || 0}%`} />
      </div>

      <div className={card}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Newsletter Controls</h3>
              <InfoTag label="Newsletter behavior settings" description="Configure default sender name, audience tags, opt-in requirements, and newsletter automation" variant="primary" />
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Set default behavior for subscriber sync and editorial automation.</p>
          </div>
          <SaveBar saving={savingSettings} onSave={saveSettings} label="Save Newsletter Settings" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Default Sender Name" value={settings.default_sender_name || ''} onChange={(value) => setSettings((prev) => ({ ...prev, default_sender_name: value }))} />
          <TextInput label="Default Audience Tag" value={settings.default_audience_tag || 'all'} onChange={(value) => setSettings((prev) => ({ ...prev, default_audience_tag: value }))} />
          <CheckboxInput label="Require double opt-in" checked={!!settings.double_opt_in} onChange={(value) => setSettings((prev) => ({ ...prev, double_opt_in: value }))} />
          <CheckboxInput label="Auto-sync to ConvertKit" checked={!!settings.auto_sync_convertkit} onChange={(value) => setSettings((prev) => ({ ...prev, auto_sync_convertkit: value }))} />
          <CheckboxInput label="Enable weekly digest" checked={!!settings.enable_weekly_digest} onChange={(value) => setSettings((prev) => ({ ...prev, enable_weekly_digest: value }))} />
        </div>
        <div className="mt-6">
          <h4 className="text-lg font-semibold text-white mb-3">Top subscriber sources</h4>
          <div className="space-y-2">
            {(dashboard.top_sources || []).map((source) => (
              <div key={source.source} className="flex items-center justify-between px-3 py-2" style={shellStyle}>
                <span style={{ color: 'var(--text-secondary)' }}>{source.source}</span>
                <span style={{ color: 'var(--accent-primary)' }}>{source.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
        <div className={card}>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Newsletter Campaign Editor</h3>
                <InfoTag label="Create and edit campaigns" description="Select an existing campaign to hydrate this form, then update, schedule, or publish it" variant="success" />
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Existing campaigns are editable in place. New campaigns can be created from this same form.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={resetCampaignForm} className="btn-secondary" disabled={savingCampaign}>Clear All Fields</button>
              <button onClick={saveCampaign} className="btn-primary" disabled={savingCampaign}>{savingCampaign ? 'Saving...' : campaignForm.id ? 'Update Campaign' : 'Save Campaign'}</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput label="Title" value={campaignForm.title} onChange={(value) => setCampaignForm((prev) => ({ ...prev, title: value }))} />
            <TextInput label="Subject" value={campaignForm.subject} onChange={(value) => setCampaignForm((prev) => ({ ...prev, subject: value }))} />
            <TextInput label="Preview Text" value={campaignForm.preview_text} onChange={(value) => setCampaignForm((prev) => ({ ...prev, preview_text: value }))} />
            <TextInput label="Audience Tag" value={campaignForm.audience_tag} onChange={(value) => setCampaignForm((prev) => ({ ...prev, audience_tag: value }))} />
            <SelectInput label="Status" value={campaignForm.status} onChange={(value) => setCampaignForm((prev) => ({ ...prev, status: value }))} options={[{ value: 'draft', label: 'Draft' }, { value: 'scheduled', label: 'Scheduled' }, { value: 'processing', label: 'Processing' }, { value: 'sent', label: 'Sent' }]} />
            <TextInput label="Scheduled For (ISO)" value={campaignForm.scheduled_for || ''} onChange={(value) => setCampaignForm((prev) => ({ ...prev, scheduled_for: value }))} placeholder="2026-01-15T14:00:00+00:00" />
            <TextInput label="CTA Label" value={campaignForm.cta_label} onChange={(value) => setCampaignForm((prev) => ({ ...prev, cta_label: value }))} />
            <TextInput label="CTA URL (relative or absolute)" value={campaignForm.cta_url} onChange={(value) => setCampaignForm((prev) => ({ ...prev, cta_url: value }))} placeholder="/newsletter" />
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-white font-semibold mb-2">Newsletter Header <span className="text-xs text-gray-500">(Read-only - standard template)</span></label>
              <textarea 
                readOnly 
                rows={6} 
                value={campaignForm.header_html} 
                style={shellStyle}
                className="w-full bg-gray-700 text-gray-300 p-2 rounded font-mono text-sm"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Header is locked to maintain brand consistency across all newsletters.</p>
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">Newsletter Content <span className="text-xs text-accent-primary">*Required</span></label>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div>
                  <textarea 
                    rows={14} 
                    value={campaignForm.content_html}
                    onChange={(e) => setCampaignForm((prev) => ({ ...prev, content_html: e.target.value }))}
                    placeholder="Paste your HTML content here. This will appear between the header and footer."
                    style={shellStyle}
                    className="w-full bg-gray-700 text-white p-2 rounded font-mono text-sm"
                  />
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Paste your HTML block here. This will be displayed between the standard header and footer.</p>
                  <p className="text-xs mt-1" style={{ color: contentHtmlValidation.valid ? '#22c55e' : '#f59e0b' }}>
                    {contentHtmlValidation.valid ? 'HTML validation: valid markup detected.' : `HTML validation: ${contentHtmlValidation.reason}`}
                  </p>
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--text-muted)' }}>Live email preview</p>
                  <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-subtle)', background: '#ffffff' }}>
                    <iframe
                      title="newsletter-html-preview"
                      srcDoc={compiledNewsletterHtml}
                      className="w-full h-[420px]"
                      sandbox="allow-popups allow-popups-to-escape-sandbox"
                      style={{ border: 'none' }}
                    />
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Preview renders header + content + footer exactly as the email payload.</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-white font-semibold mb-2">Newsletter Footer <span className="text-xs text-gray-500">(Read-only - standard template)</span></label>
              <textarea 
                readOnly 
                rows={6} 
                value={campaignForm.footer_html} 
                style={shellStyle}
                className="w-full bg-gray-700 text-gray-300 p-2 rounded font-mono text-sm"
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>Footer is locked to maintain legal compliance and brand consistency.</p>
            </div>
          </div>
        </div>

        <ListCard
          title="Existing Campaigns"
          items={dashboard.campaigns || []}
          emptyMessage={loading ? 'Loading campaigns...' : 'No campaigns yet.'}
          renderItem={(item) => (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-white font-semibold">{item.title || 'Untitled campaign'}</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.subject || 'No subject'}</p>
                </div>
                <StatusPill status={item.status || 'draft'} />
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.preview_text || 'No preview text.'}</p>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                Scheduled: {item.scheduled_for || 'not set'}
              </div>
              <div className="flex flex-wrap gap-2">
                <button className="btn-secondary text-xs" onClick={() => editCampaign(item)}>Edit</button>
                <button className="btn-secondary text-xs" onClick={() => automateCampaign(item.id, 'schedule')} disabled={savingCampaign}>Schedule</button>
                <button className="btn-secondary text-xs" onClick={() => automateCampaign(item.id, 'publish_now')} disabled={savingCampaign}>Publish Now</button>
                <button className="btn-danger text-xs" onClick={() => deleteCampaign(item.id)} disabled={savingCampaign}>Delete</button>
              </div>
            </div>
          )}
        />
      </div>
    </motion.div>
  );
};

export const ChangelogsAdminTab = ({ tools = [] }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ tool_id: '', title: '', version_label: '', summary: '', body: '', status: 'published', release_date: '', change_type: 'feature', source_url: '' });

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getChangelogs();
      setItems(response.data?.items || []);
    } catch {
      alert('Failed to load changelogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveChangelog(form);
      setForm({ tool_id: '', title: '', version_label: '', summary: '', body: '', status: 'published', release_date: '', change_type: 'feature', source_url: '' });
      await load();
    } catch {
      alert('Failed to save changelog');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...sectionMotion} className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6" data-testid="changelogs-admin-tab">
      <div className={card}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Tool Changelog Publisher</h3>
              <InfoTag label="Publish tool version updates" description="Document and publish version releases, feature announcements, bugfixes, and pricing changes" variant="primary" />
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>These entries feed tool pages so product changes are visible without editing the tool payload directly.</p>
          </div>
          <SaveBar saving={saving} onSave={save} label="Save Entry" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Tool"
            value={form.tool_id}
            onChange={(value) => setForm((prev) => ({ ...prev, tool_id: value }))}
            options={[{ value: '', label: 'Select tool' }, ...tools.map((tool) => ({ value: tool.id, label: tool.name }))]}
          />
          <TextInput label="Version Label" value={form.version_label} onChange={(value) => setForm((prev) => ({ ...prev, version_label: value }))} placeholder="v2.3" />
          <TextInput label="Title" value={form.title} onChange={(value) => setForm((prev) => ({ ...prev, title: value }))} />
          <TextInput label="Release Date (ISO)" value={form.release_date} onChange={(value) => setForm((prev) => ({ ...prev, release_date: value }))} />
          <SelectInput
            label="Status"
            value={form.status}
            onChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
            options={[{ value: 'published', label: 'Published' }, { value: 'queued', label: 'Queued' }, { value: 'draft', label: 'Draft' }]}
          />
          <SelectInput
            label="Change Type"
            value={form.change_type}
            onChange={(value) => setForm((prev) => ({ ...prev, change_type: value }))}
            options={[{ value: 'feature', label: 'Feature' }, { value: 'improvement', label: 'Improvement' }, { value: 'bugfix', label: 'Bugfix' }, { value: 'pricing', label: 'Pricing' }]}
          />
          <div className="md:col-span-2">
            <TextInput label="Source URL" value={form.source_url} onChange={(value) => setForm((prev) => ({ ...prev, source_url: value }))} />
          </div>
          <div className="md:col-span-2">
            <TextArea label="Summary" value={form.summary} onChange={(value) => setForm((prev) => ({ ...prev, summary: value }))} rows={3} />
          </div>
          <div className="md:col-span-2">
            <TextArea label="Body" value={form.body} onChange={(value) => setForm((prev) => ({ ...prev, body: value }))} rows={8} />
          </div>
        </div>
      </div>
      <ListCard
        title="Recent Entries"
        items={items}
        emptyMessage={loading ? 'Loading changelogs...' : 'No changelogs yet.'}
        renderItem={(item) => (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="text-white font-semibold">{item.title}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.tool_name || 'Unassigned'} {item.version_label ? `- ${item.version_label}` : ''}</p>
              </div>
              <StatusPill status={item.status} />
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.summary || 'No summary provided.'}</p>
          </div>
        )}
      />
    </motion.div>
  );
};

export const SeoPagesAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({});
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', slug: '', page_type: 'comparison', target_keyword: '', search_intent: 'commercial', status: 'draft', excerpt: '', body: '', related_tool_slugs: '', canonical_url: '', schema_type: 'Article' });

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getSeoPages();
      setItems(response.data?.items || []);
      setSummary(response.data?.summary || {});
    } catch {
      alert('Failed to load SEO pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveSeoPage({ ...form, related_tool_slugs: splitCsv(form.related_tool_slugs) });
      setForm({ title: '', slug: '', page_type: 'comparison', target_keyword: '', search_intent: 'commercial', status: 'draft', excerpt: '', body: '', related_tool_slugs: '', canonical_url: '', schema_type: 'Article' });
      await load();
    } catch {
      alert('Failed to save SEO page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="seo-pages-admin-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Published" value={summary.published || 0} />
        <MetricCard label="Drafts" value={summary.drafts || 0} />
        <MetricCard label="Comparison Pages" value={summary.comparison_pages || 0} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
        <div className={card}>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Programmatic SEO Page Builder</h3>
                <InfoTag label="Create Editorial SEO content" description="Build comparison, category, and use-case pages optimized for search with keyword targeting and schema markup" variant="primary" />
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Create publication-ready landing pages that can later be syndicated or linked from comparison funnels.</p>
            </div>
            <SaveBar saving={saving} onSave={save} label="Save SEO Page" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput label="Title" value={form.title} onChange={(value) => setForm((prev) => ({ ...prev, title: value }))} />
            <TextInput label="Slug" value={form.slug} onChange={(value) => setForm((prev) => ({ ...prev, slug: value }))} />
            <SelectInput label="Page Type" value={form.page_type} onChange={(value) => setForm((prev) => ({ ...prev, page_type: value }))} options={[{ value: 'comparison', label: 'Comparison' }, { value: 'category', label: 'Category' }, { value: 'use-case', label: 'Use Case' }, { value: 'editorial', label: 'Editorial' }]} />
            <SelectInput label="Intent" value={form.search_intent} onChange={(value) => setForm((prev) => ({ ...prev, search_intent: value }))} options={[{ value: 'commercial', label: 'Commercial' }, { value: 'informational', label: 'Informational' }, { value: 'navigational', label: 'Navigational' }]} />
            <TextInput label="Target Keyword" value={form.target_keyword} onChange={(value) => setForm((prev) => ({ ...prev, target_keyword: value }))} />
            <SelectInput label="Status" value={form.status} onChange={(value) => setForm((prev) => ({ ...prev, status: value }))} options={[{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }]} />
            <TextInput label="Canonical URL" value={form.canonical_url} onChange={(value) => setForm((prev) => ({ ...prev, canonical_url: value }))} />
            <TextInput label="Schema Type" value={form.schema_type} onChange={(value) => setForm((prev) => ({ ...prev, schema_type: value }))} />
            <div className="md:col-span-2">
              <TextInput label="Related Tool Slugs" value={form.related_tool_slugs} onChange={(value) => setForm((prev) => ({ ...prev, related_tool_slugs: value }))} placeholder="chatgpt, claude, grammarly" />
            </div>
            <div className="md:col-span-2">
              <TextArea label="Excerpt" value={form.excerpt} onChange={(value) => setForm((prev) => ({ ...prev, excerpt: value }))} rows={3} />
            </div>
            <div className="md:col-span-2">
              <TextArea label="Body" value={form.body} onChange={(value) => setForm((prev) => ({ ...prev, body: value }))} rows={10} />
            </div>
          </div>
        </div>
        <ListCard
          title="SEO Pages"
          items={items}
          emptyMessage={loading ? 'Loading SEO pages...' : 'No SEO pages yet.'}
          renderItem={(item) => (
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-white font-semibold">{item.title}</p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>/resources/{item.slug}</p>
                </div>
                <StatusPill status={item.status} />
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.target_keyword || 'No keyword assigned'}</p>
            </div>
          )}
        />
      </div>
    </motion.div>
  );
};

export const FitScoresAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [settings, setSettings] = useState({ rating_weight: 0.25, review_weight: 0.15, pricing_weight: 0.15, adoption_weight: 0.15, roi_weight: 0.15, implementation_weight: 0.15 });

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getFitScores();
      setItems(response.data?.items || []);
      setSummary(response.data?.summary || {});
      setSettings((prev) => ({ ...prev, ...(response.data?.settings || {}) }));
    } catch {
      alert('Failed to load fit scores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateFitScoreSettings(settings);
      await load();
    } catch {
      alert('Failed to save fit score settings');
    } finally {
      setSaving(false);
    }
  };

  const weights = [
    ['rating_weight', 'Rating'],
    ['review_weight', 'Reviews'],
    ['pricing_weight', 'Pricing'],
    ['adoption_weight', 'Adoption'],
    ['roi_weight', 'ROI'],
    ['implementation_weight', 'Implementation'],
  ];

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="fit-scores-admin-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Average Fit" value={summary.avg_fit || 0} />
        <MetricCard label="High Fit Tools" value={summary.top_count || 0} />
        <MetricCard label="Low Fit Tools" value={summary.low_count || 0} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <div className={card}>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Score Weighting</h3>
                <InfoTag label="Tune fit algorithm weights" description="Adjust recommendation algorithm by balancing rating, reviews, pricing, adoption, ROI, and implementation factors" variant="primary" />
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Tune recommendation logic by balancing trust, cost, adoption, and implementation risk.</p>
            </div>
            <SaveBar saving={saving} onSave={save} label="Recalculate & Save" />
          </div>
          <div className="space-y-4">
            {weights.map(([key, label]) => (
              <label key={key} className="block text-sm" style={{ color: 'var(--text-secondary)' }}>
                {label}
                <input type="number" min="0" max="1" step="0.05" value={settings[key]} onChange={(event) => setSettings((prev) => ({ ...prev, [key]: Number(event.target.value) }))} className="w-full mt-2 px-3 py-2 bg-transparent border border-white/20 text-white" />
              </label>
            ))}
          </div>
        </div>
        <ListCard
          title="Top Fit Scores"
          items={items.slice(0, 20)}
          emptyMessage={loading ? 'Loading fit scores...' : 'No fit scores available.'}
          renderItem={(item) => (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold">{item.tool_name}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.category_name || 'Uncategorized'}</p>
              </div>
              <div className="text-right">
                <p className="font-mono" style={{ color: 'var(--accent-primary)' }}>{item.fit_score}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>ROI {item.signals?.roi || 0} / Price {item.signals?.price || 0}</p>
              </div>
            </div>
          )}
        />
      </div>
    </motion.div>
  );
};

export const PricingMonitorAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [dashboard, setDashboard] = useState({ summary: {}, alerts: [], snapshots: [] });

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getPricingMonitor();
      setDashboard(response.data || { summary: {}, alerts: [], snapshots: [] });
    } catch {
      alert('Failed to load pricing monitor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const capture = async () => {
    setCapturing(true);
    try {
      await adminApi.capturePricingSnapshot({ note: 'manual dashboard capture' });
      await load();
    } catch {
      alert('Failed to capture pricing snapshot');
    } finally {
      setCapturing(false);
    }
  };

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="pricing-monitor-admin-tab">
      <div className="flex justify-end">
        <button onClick={capture} disabled={capturing} className="btn-primary">{capturing ? 'Capturing...' : 'Capture Snapshot'}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard label="Snapshots" value={dashboard.summary?.snapshots || 0} />
        <MetricCard label="Alerts" value={dashboard.summary?.alerts || 0} />
        <MetricCard label="Open Alerts" value={dashboard.summary?.recent_alerts || 0} />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ListCard
          title="Pricing Alerts"
          items={dashboard.alerts || []}
          emptyMessage={loading ? 'Loading pricing alerts...' : 'No pricing alerts yet.'}
          renderItem={(item) => (
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-white font-semibold">{item.tool_name}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.message}</p>
              </div>
              <StatusPill status={item.status} />
            </div>
          )}
        />
        <ListCard
          title="Recent Snapshots"
          items={(dashboard.snapshots || []).slice(0, 20)}
          emptyMessage={loading ? 'Loading snapshots...' : 'No snapshots captured yet.'}
          renderItem={(item) => (
            <div>
              <p className="text-white font-semibold">{item.tool_name}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.snapshot?.monthly_price_estimate || 'Custom pricing'} - {item.captured_at}</p>
            </div>
          )}
        />
      </div>
    </motion.div>
  );
};

export const HealthMonitorAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({ checks: [], summary: {} });
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [healthRes, eventsRes] = await Promise.all([
          adminApi.getHealthMonitor(),
          adminApi.getSystemEvents({ limit: 25 }).catch(() => ({ data: { items: [] } })),
        ]);
        setDashboard(healthRes.data || { checks: [], summary: {} });
        setEvents(eventsRes.data?.items || []);
      } catch {
        alert('Failed to load health monitor');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="health-monitor-admin-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard label="Configured" value={dashboard.summary?.configured_integrations || 0} />
        <MetricCard label="Missing" value={dashboard.summary?.missing_integrations || 0} />
        <MetricCard label="Critical Alerts" value={dashboard.summary?.critical_action_items || 0} />
        <MetricCard label="Queued Content" value={dashboard.summary?.queued_content || 0} />
        <MetricCard label="Pending Reviews" value={dashboard.summary?.pending_reviews || 0} />
        <MetricCard label="Consulting Leads" value={dashboard.summary?.consulting_leads || 0} />
        <MetricCard label="Stale SEO Drafts" value={dashboard.summary?.stale_seo_pages || 0} />
      </div>
      <ListCard
        title="Immediate Action Items"
        items={dashboard.action_items || []}
        emptyMessage={loading ? 'Loading action items...' : 'No immediate action items.'}
        renderItem={(item) => (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{item.title}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.detail}</p>
            </div>
            <StatusPill status={item.severity || 'info'} />
          </div>
        )}
      />
      <ListCard
        title="Integration Status"
        items={dashboard.checks || []}
        emptyMessage={loading ? 'Loading checks...' : 'No checks available.'}
        renderItem={(item) => (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{item.name}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.detail}</p>
            </div>
            <StatusPill status={item.status} />
          </div>
        )}
      />
      <ListCard
        title="Recent System Diagnostics"
        items={events}
        emptyMessage={loading ? 'Loading diagnostics...' : 'No system diagnostics yet.'}
        renderItem={(item) => (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{item.component}: {item.event_type}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.message}</p>
            </div>
            <StatusPill status={item.severity || 'info'} />
          </div>
        )}
      />
    </motion.div>
  );
};

export const LoggingAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [activeCategory, setActiveCategory] = useState('tool_changes');
  const [aggregationPeriod, setAggregationPeriod] = useState('day');
  const [purgeBeforeDate, setPurgeBeforeDate] = useState('');
  const [purgeLogType, setPurgeLogType] = useState('all');
  const [state, setState] = useState({
    auditLogs: [],
    changelogs: [],
    events: [],
    auditAggregate: [],
    systemAggregate: [],
    auditUnavailable: false,
    alerts: { critical: [], warnings: [] },
    checks: [],
    actionItems: [],
    diagnostics: [],
    aiSettings: { key_status: {} },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [
        auditLogsRes,
        changelogsRes,
        eventsRes,
        auditAggregateRes,
        systemAggregateRes,
        alertsRes,
        healthRes,
        diagnosticsRes,
        aiSettingsRes,
      ] = await Promise.all([
        adminApi.getAuditLogs({ limit: 300 }).catch((error) => ({ data: { items: [] }, __error: error })),
        adminApi.getChangelogs().catch(() => ({ data: { items: [] } })),
        adminApi.getSystemEvents({ limit: 300, include_noise: false }).catch(() => ({ data: { items: [] } })),
        adminApi.getAuditLogsAggregate({ period: aggregationPeriod }).catch(() => ({ data: { items: [] } })),
        adminApi.getSystemEventsAggregate({ period: aggregationPeriod }).catch(() => ({ data: { items: [] } })),
        adminApi.getSystemAlerts().catch(() => ({ data: { critical: [], warnings: [] } })),
        adminApi.getHealthMonitor().catch(() => ({ data: { checks: [], action_items: [] } })),
        adminApi.validateOpsConfig().catch(() => ({ data: { checks: [] } })),
        adminApi.getAiSettings().catch(() => ({ data: { key_status: {} } })),
      ]);

      setState({
        auditLogs: auditLogsRes.data?.items || [],
        changelogs: changelogsRes.data?.items || [],
        events: eventsRes.data?.items || [],
        auditAggregate: auditAggregateRes.data?.items || [],
        systemAggregate: systemAggregateRes.data?.items || [],
        auditUnavailable: [404, 405].includes(auditLogsRes.__error?.response?.status),
        alerts: {
          critical: alertsRes.data?.critical || [],
          warnings: alertsRes.data?.warnings || [],
        },
        checks: healthRes.data?.checks || [],
        actionItems: healthRes.data?.action_items || [],
        diagnostics: diagnosticsRes.data?.checks || [],
        aiSettings: aiSettingsRes.data || { key_status: {} },
      });
    } catch {
      alert('Failed to load logging center');
    } finally {
      setLoading(false);
    }
  }, [aggregationPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  const integrationChecks = state.checks.filter((check) => integrationNames.includes(check.name));
  const diagnosticsChecks = state.diagnostics || [];
  const criticalEventItems = (state.events || []).filter((event) => ['critical', 'fatal'].includes(toLowerText(event.severity)));
  const failureEventItems = (state.events || []).filter((event) => ['error', 'warning', 'critical'].includes(toLowerText(event.severity)) || toLowerText(event.fallback_status) === 'degraded');
  const developmentIssueItems = (state.events || []).filter((event) => looksLikeDevelopmentIssue(event));
  const apiIssueEvents = (state.events || []).filter((event) => looksLikeApiIssue(event));
  const environmentIssueEvents = (state.events || []).filter((event) => looksLikeEnvironmentIssue(event));

  const keyStatus = state.aiSettings?.key_status || {};
  const keyEntries = ['anthropic', 'openai', 'gemini', 'grok'].map((provider) => ({
    provider,
    configured: !!keyStatus?.[provider]?.configured,
    masked: keyStatus?.[provider]?.masked || '',
  }));

  const keyRiskItems = keyEntries.map((entry) => {
    if (!entry.configured) {
      return {
        id: `key-${entry.provider}`,
        severity: 'critical',
        title: `${entry.provider.toUpperCase()} key missing`,
        detail: 'Provider unavailable in fallback chain until key is configured.',
      };
    }
    return {
      id: `key-${entry.provider}`,
      severity: 'warning',
      title: `${entry.provider.toUpperCase()} key configured`,
      detail: 'No expiration metadata detected. Track rotation schedule externally and set periodic validation checks.',
    };
  });

  const environmentChecks = [
    ...integrationChecks,
    ...diagnosticsChecks,
  ].filter((item) => ['missing', 'failed'].includes(toLowerText(item.status)) || toLowerText(item.detail).includes('failed') || toLowerText(item.detail).includes('missing'));

  const auditItems = (state.auditLogs || []).filter((item) => ['tool', 'category'].includes(toLowerText(item.entity_type)));
  const fallbackChangelogItems = (state.changelogs || []).map((item) => ({
    id: item.id,
    entity_type: 'tool',
    entity_id: item.tool_id,
    entity_name: item.tool_name || item.tool_id || 'Unknown tool',
    action: item.change_type || item.status || 'change',
    actor: 'system-changelog',
    created_at: item.release_date || item.updated_at || item.created_at,
    changes: [
      {
        field: 'changelog',
        before: null,
        after: item.summary || item.title || item.body || 'Change recorded in changelog',
      },
    ],
  }));
  const toolCategoryAuditItems = auditItems.length ? auditItems : fallbackChangelogItems;

  const groupedFailureItems = Object.values(
    (failureEventItems || []).reduce((acc, item) => {
      const key = buildFailureFingerprint(item);
      if (!acc[key]) {
        acc[key] = {
          ...item,
          frequency: 0,
          first_seen: item.created_at || null,
          latest_at: item.created_at || null,
        };
      }
      acc[key].frequency += 1;
      if (item.created_at && (!acc[key].latest_at || item.created_at > acc[key].latest_at)) {
        acc[key].latest_at = item.created_at;
      }
      if (item.created_at && (!acc[key].first_seen || item.created_at < acc[key].first_seen)) {
        acc[key].first_seen = item.created_at;
      }
      return acc;
    }, {})
  ).sort((a, b) => {
    if (b.frequency !== a.frequency) return b.frequency - a.frequency;
    return String(b.latest_at || '').localeCompare(String(a.latest_at || ''));
  });

  const observabilityGaps = [];
  if ((toolCategoryAuditItems || []).length === 0) {
    observabilityGaps.push('No tool/category audit records detected. Enforce audit entry on every catalog mutation.');
  }
  if ((state.events || []).length === 0) {
    observabilityGaps.push('No system events recorded. Ensure emit_system_event is called for all exception paths.');
  }
  if (!state.aiSettings?.key_status || Object.keys(state.aiSettings.key_status).length === 0) {
    observabilityGaps.push('AI key status unavailable. Confirm runtime settings endpoint access and secure key-status visibility.');
  }
  if (!diagnosticsChecks.length) {
    observabilityGaps.push('Ops diagnostics checks are empty. Schedule periodic diagnostics and persist snapshots.');
  }

  const categoryTabs = [
    { id: 'tool_changes', label: `Tool+Category Audit (${toolCategoryAuditItems.length})` },
    { id: 'development_issues', label: `Development Issues (${developmentIssueItems.length})` },
    { id: 'failure_issues', label: `Failure Issues (${groupedFailureItems.length})` },
    { id: 'critical_failures', label: `Critical Failures (${criticalEventItems.length + (state.alerts.critical || []).length})` },
    { id: 'api_keys_integrations', label: 'API Keys & Integrations' },
    { id: 'environment_access', label: 'Environment & Access' },
    { id: 'observability_principles', label: 'Observability Principles' },
  ];

  const deleteAuditGroup = async (bucket) => {
    const confirmed = window.confirm(`Delete audit logs for ${aggregationPeriod} bucket ${bucket}?`);
    if (!confirmed) return;
    setBusy(true);
    try {
      await adminApi.deleteAuditLogsGroup({ period: aggregationPeriod, bucket });
      await load();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete audit log bucket');
    } finally {
      setBusy(false);
    }
  };

  const deleteSystemGroup = async (bucket) => {
    const confirmed = window.confirm(`Delete system events for ${aggregationPeriod} bucket ${bucket}?`);
    if (!confirmed) return;
    setBusy(true);
    try {
      await adminApi.deleteSystemEventsGroup({ period: aggregationPeriod, bucket });
      await load();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete system event bucket');
    } finally {
      setBusy(false);
    }
  };

  const purgeByDate = async () => {
    if (!purgeBeforeDate) {
      alert('Select a retention cutoff date first.');
      return;
    }
    const cutoff = `${purgeBeforeDate}T00:00:00+00:00`;
    const confirmed = window.confirm(`Purge ${purgeLogType} logs older than ${cutoff}?`);
    if (!confirmed) return;
    setBusy(true);
    try {
      const requests = [];
      if (purgeLogType === 'all' || purgeLogType === 'audit') {
        requests.push(adminApi.purgeAuditLogs({ before_date: cutoff }));
      }
      if (purgeLogType === 'all' || purgeLogType === 'system') {
        requests.push(adminApi.purgeSystemEvents({ before_date: cutoff }));
      }
      await Promise.all(requests);
      await load();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to purge logs by date');
    } finally {
      setBusy(false);
    }
  };

  const renderToolChanges = () => (
    <ListCard
      title="Audit Trail: Tool & Category Changes"
      items={toolCategoryAuditItems.slice(0, 120)}
      emptyMessage={loading ? 'Loading audit trail...' : 'No tool/category audit changes recorded yet.'}
      renderItem={(item) => (
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-white font-semibold">{item.entity_type || 'entity'}: {item.entity_name || item.entity_id || 'Unknown'} ({item.action || 'update'})</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>By {item.actor || 'unknown'} | {item.created_at || 'Timestamp unavailable'}</p>
            <div className="mt-2 space-y-1">
              {(item.changes || []).slice(0, 6).map((change, idx) => (
                <p key={`${item.id || item.entity_id}-${change.field}-${idx}`} className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {change.field}: {JSON.stringify(change.before)}{' -> '}{JSON.stringify(change.after)}
                </p>
              ))}
              {(item.changes || []).length > 6 && (
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>+ {(item.changes || []).length - 6} more field changes</p>
              )}
            </div>
          </div>
          <StatusPill status={item.action || 'update'} />
        </div>
      )}
    />
  );

  const renderDevelopmentIssues = () => (
    <ListCard
      title="Development & Deployment Issues"
      items={(developmentIssueItems || []).slice(0, 80)}
      emptyMessage={loading ? 'Loading development issues...' : 'No development issues currently captured.'}
      renderItem={(item) => (
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-white font-semibold">{item.component || 'unknown'}: {item.event_type || 'event'}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.message || 'No message'}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{item.created_at || 'Timestamp unavailable'}</p>
          </div>
          <StatusPill status={item.severity || 'info'} />
        </div>
      )}
    />
  );

  const renderFailureIssues = () => (
    <ListCard
      title="Failure / Degradation Events"
      items={(groupedFailureItems || []).slice(0, 120)}
      emptyMessage={loading ? 'Loading failure events...' : 'No failure events in the selected window.'}
      renderItem={(item) => (
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-white font-semibold">{item.component || 'unknown'}: {item.event_type || 'event'}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.message || 'No message'}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Frequency: {item.frequency || 1} | Fallback: {item.fallback_status || 'none'} | Latest: {item.latest_at || item.created_at || 'Timestamp unavailable'}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>First seen: {item.first_seen || 'n/a'}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Suggested fix: {getFailureSolutionHint(item)}</p>
          </div>
          <StatusPill status={item.severity || 'warning'} />
        </div>
      )}
    />
  );

  const renderCriticalFailures = () => {
    const criticalItems = [
      ...(state.alerts.critical || []).map((item, index) => ({ ...item, id: item.id || `alert-${index}`, source: 'alert' })),
      ...criticalEventItems.map((item, index) => ({ ...item, id: item.id || `event-${index}`, source: 'event' })),
    ];
    return (
      <ListCard
        title="Critical Incidents"
        items={criticalItems}
        emptyMessage={loading ? 'Loading critical incidents...' : 'No critical incidents detected.'}
        renderItem={(item) => (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{item.title || `${item.component || 'unknown'}: ${item.event_type || 'event'}`}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.detail || item.message || 'No details available.'}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{item.generated_at || item.created_at || 'Timestamp unavailable'} | Source: {item.source || 'health-monitor'}</p>
            </div>
            <StatusPill status={item.severity || 'critical'} />
          </div>
        )}
      />
    );
  };

  const renderApiAndKeys = () => {
    const apiIssues = [
      ...apiIssueEvents.slice(0, 40),
      ...integrationChecks.filter((check) => ['missing', 'failed'].includes(toLowerText(check.status))).map((check) => ({
        id: `integration-${check.name}`,
        component: 'integration',
        event_type: check.name,
        message: check.detail,
        severity: check.status,
        created_at: 'latest',
      })),
    ];

    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ListCard
          title="API & Integration Issues"
          items={apiIssues}
          emptyMessage={loading ? 'Loading API issues...' : 'No API issues in latest diagnostics.'}
          renderItem={(item) => (
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-white font-semibold">{item.component || 'api'}: {item.event_type || 'event'}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.message || 'No details available.'}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{item.created_at || 'Timestamp unavailable'}</p>
              </div>
              <StatusPill status={item.severity || 'warning'} />
            </div>
          )}
        />
        <ListCard
          title="API Key Risk Register"
          items={keyRiskItems}
          emptyMessage="No key risk data available."
          renderItem={(item) => (
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-white font-semibold">{item.title}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.detail}</p>
              </div>
              <StatusPill status={item.severity} />
            </div>
          )}
        />
      </div>
    );
  };

  const renderEnvironmentAndAccess = () => {
    const environmentIssues = [
      ...environmentIssueEvents.slice(0, 40),
      ...environmentChecks.map((item, index) => ({
        id: `check-${index}`,
        component: item.name || 'environment',
        event_type: item.status || 'status',
        message: item.detail || 'No details available.',
        severity: item.status || 'warning',
      })),
    ];

    return (
      <ListCard
        title="Environment & Access Issues"
        items={environmentIssues}
        emptyMessage={loading ? 'Loading environment/access issues...' : 'No environment or access issues detected.'}
        renderItem={(item) => (
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-white font-semibold">{item.component || 'environment'}: {item.event_type || 'issue'}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.message || 'No details available.'}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{item.created_at || 'Current diagnostic snapshot'}</p>
            </div>
            <StatusPill status={item.severity || 'warning'} />
          </div>
        )}
      />
    );
  };

  const renderPrinciples = () => (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <div className={card}>
        <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Production Logging Principles</h3>
        <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
          <li>- Correlation IDs: every request, workflow run, and admin action should carry a trace ID.</li>
          <li>- Structured logs: use typed JSON with component, severity, event_type, and metadata.</li>
          <li>- Severity discipline: info for state transitions, warning for degradations, error for failures, critical for incident paging.</li>
          <li>- Immutable audit trail: preserve tool and settings changes with actor and timestamp.</li>
          <li>- Integration observability: monitor API connectivity, auth failures, retries, and fallback behavior.</li>
          <li>- Security logging: include access denied events, suspicious auth patterns, and secret-usage anomalies.</li>
          <li>- Retention policy: maintain hot logs for triage and archive for postmortems and compliance.</li>
          <li>- Actionability: every alert should include owner, remediation hint, and escalation level.</li>
        </ul>
      </div>
      <div className={card}>
        <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>Coverage Gaps & Next Additions</h3>
        {observabilityGaps.length === 0 ? (
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No immediate observability gaps detected from current telemetry sources.</p>
        ) : (
          <ul className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            {observabilityGaps.map((gap) => (
              <li key={gap}>- {gap}</li>
            ))}
          </ul>
        )}
        <div className="mt-4 p-3" style={shellStyle}>
          <p className="text-xs uppercase font-mono mb-2" style={{ color: 'var(--text-muted)' }}>Recommended additional sections</p>
          <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <li>- Release markers and deployment timeline view</li>
            <li>- Auth and authorization audit stream</li>
            <li>- DB latency and query timeout dashboard</li>
            <li>- External dependency uptime matrix with SLO trends</li>
            <li>- Incident postmortem tracker linked to critical events</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderCategoryBody = () => {
    if (activeCategory === 'tool_changes') return renderToolChanges();
    if (activeCategory === 'development_issues') return renderDevelopmentIssues();
    if (activeCategory === 'failure_issues') return renderFailureIssues();
    if (activeCategory === 'critical_failures') return renderCriticalFailures();
    if (activeCategory === 'api_keys_integrations') return renderApiAndKeys();
    if (activeCategory === 'environment_access') return renderEnvironmentAndAccess();
    return renderPrinciples();
  };

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="logging-admin-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Logging Center</h2>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>High-signal audit trail and observability logs for fast production diagnosis.</p>
        </div>
        <button onClick={load} disabled={loading} className="btn-secondary inline-flex items-center gap-2">
          {loading ? 'Refreshing...' : 'Refresh Logs'}
        </button>
      </div>

      <div className={card}>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <p className="text-xs uppercase font-mono mb-1" style={{ color: 'var(--text-muted)' }}>Aggregate Frequency</p>
            <select
              value={aggregationPeriod}
              onChange={(event) => setAggregationPeriod(event.target.value)}
              className="input-pro"
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
            </select>
          </div>
          <div>
            <p className="text-xs uppercase font-mono mb-1" style={{ color: 'var(--text-muted)' }}>Purge Log Type</p>
            <select
              value={purgeLogType}
              onChange={(event) => setPurgeLogType(event.target.value)}
              className="input-pro"
            >
              <option value="all">All</option>
              <option value="audit">Audit logs only</option>
              <option value="system">System events only</option>
            </select>
          </div>
          <div>
            <p className="text-xs uppercase font-mono mb-1" style={{ color: 'var(--text-muted)' }}>Auto-delete before date</p>
            <input
              type="date"
              value={purgeBeforeDate}
              onChange={(event) => setPurgeBeforeDate(event.target.value)}
              className="input-pro"
            />
          </div>
          <button onClick={purgeByDate} disabled={busy} className="btn-secondary">
            {busy ? 'Working...' : 'Purge Old Logs'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ListCard
          title={`Audit Log Groups (${aggregationPeriod})`}
          items={state.auditAggregate || []}
          emptyMessage={loading ? 'Loading audit aggregates...' : 'No audit groups available.'}
          renderItem={(item) => (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold">{item.bucket}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.count} records | {item.first_seen}{' -> '}{item.last_seen}</p>
              </div>
              <button onClick={() => deleteAuditGroup(item.bucket)} disabled={busy} className="btn-secondary text-xs">Delete Group</button>
            </div>
          )}
        />
        <ListCard
          title={`System Event Groups (${aggregationPeriod})`}
          items={state.systemAggregate || []}
          emptyMessage={loading ? 'Loading event aggregates...' : 'No event groups available.'}
          renderItem={(item) => (
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-white font-semibold">{item.bucket}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.count} records | {item.first_seen}{' -> '}{item.last_seen}</p>
              </div>
              <button onClick={() => deleteSystemGroup(item.bucket)} disabled={busy} className="btn-secondary text-xs">Delete Group</button>
            </div>
          )}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
        <MetricCard label="Audit Changes" value={toolCategoryAuditItems.length} />
        <MetricCard label="Development Issues" value={developmentIssueItems.length} />
        <MetricCard label="Failures" value={groupedFailureItems.length} />
        <MetricCard label="Critical" value={criticalEventItems.length + (state.alerts.critical || []).length} />
        <MetricCard label="API Issues" value={apiIssueEvents.length} />
        <MetricCard label="Env Issues" value={environmentChecks.length + environmentIssueEvents.length} />
        <MetricCard label="Missing Keys" value={keyEntries.filter((item) => !item.configured).length} />
      </div>

      {state.auditUnavailable && (
        <div className={card}>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Audit log endpoint is unavailable in the running backend process. Showing changelog fallback until backend routes are reloaded.
          </p>
        </div>
      )}

      <div className={card}>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {categoryTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveCategory(tab.id)}
              className="px-3 py-2 whitespace-nowrap text-sm transition-colors"
              style={{
                background: activeCategory === tab.id ? 'var(--accent-primary)' : 'transparent',
                color: activeCategory === tab.id ? '#000' : 'var(--text-secondary)',
                border: `1px solid ${activeCategory === tab.id ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {renderCategoryBody()}
    </motion.div>
  );
};

export const SponsorshipsAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [form, setForm] = useState({ sponsor_name: '', slot: 'homepage-hero', target_url: '', status: 'active', badge_label: 'Sponsored', notes: '', start_date: '', end_date: '' });
  const [spotlight, setSpotlight] = useState({
    tool_id: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    price: 299,
    enabled: false,
  });
  const [allTools, setAllTools] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [sponsorRes, settingsRes, toolsRes] = await Promise.all([
        adminApi.getSponsorships().catch(() => ({ data: { items: [] } })),
        adminApi.getSiteSettings().catch(() => ({ data: {} })),
        publicApi.getAllTools(false).catch(() => ({ data: [] })),
      ]);
      setItems(sponsorRes.data?.items || []);
      const settings = settingsRes.data || {};
      setSiteSettings(settings);
      setSpotlight(prev => ({
        ...prev,
        tool_id: settings.spotlight_tool_id || '',
        start_date: settings.spotlight_start_date || new Date().toISOString().split('T')[0],
        end_date: settings.spotlight_end_date || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        price: settings.spotlight_price || 299,
        enabled: settings.spotlight_enabled || false,
      }));
      const normalizedTools = Array.isArray(toolsRes.data)
        ? toolsRes.data
        : Array.isArray(toolsRes.data?.items)
          ? toolsRes.data.items
          : [];
      setAllTools(normalizedTools);
    } catch {
      alert('Failed to load sponsorships');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveSponsorship(form);
      setForm({ sponsor_name: '', slot: 'homepage-hero', target_url: '', status: 'active', badge_label: 'Sponsored', notes: '', start_date: '', end_date: '' });
      await load();
    } catch {
      alert('Failed to save sponsorship');
    } finally {
      setSaving(false);
    }
  };

  const saveSpotlight = async () => {
    setSaving(true);
    try {
      await adminApi.updateSiteSettings({
        spotlight_tool_id: spotlight.tool_id,
        spotlight_start_date: spotlight.start_date,
        spotlight_end_date: spotlight.end_date,
        spotlight_price: spotlight.price,
        spotlight_enabled: spotlight.enabled,
      });
      alert('Spotlight hero tool updated successfully');
      await load();
    } catch {
      alert('Failed to save spotlight');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...sectionMotion} className="space-y-6" data-testid="sponsorships-admin-tab">
      {/* Highlight: Paid Tool Spotlight for Hero Section */}
      <div className={card}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Hero Spotlight Tool</h3>
              <InfoTag label="Premium featured placement" description="Feature a tool in the homepage hero spotlight for paid weekly or monthly sponsored placement" variant="warning" />
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Feature a paid tool in the homepage hero "focus month" list for week or month-long paid sponsorship.</p>
          </div>
          <SaveBar saving={saving} onSave={saveSpotlight} label="Save Spotlight" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SelectInput
            label="Tool to Feature"
            value={spotlight.tool_id}
            onChange={(value) => setSpotlight(prev => ({ ...prev, tool_id: value }))}
            options={[
              { value: '', label: '- Select a tool -' },
              ...allTools.map(t => ({ value: t.id || t.slug, label: t.name }))
            ]}
          />
          <TextInput
            label="Spotlight Price ($)"
            type="number"
            value={spotlight.price}
            onChange={(value) => setSpotlight(prev => ({ ...prev, price: Number(value) }))}
          />
          <TextInput
            label="Start Date"
            type="date"
            value={spotlight.start_date}
            onChange={(value) => setSpotlight(prev => ({ ...prev, start_date: value }))}
          />
          <TextInput
            label="End Date"
            type="date"
            value={spotlight.end_date}
            onChange={(value) => setSpotlight(prev => ({ ...prev, end_date: value }))}
          />
          <div className="md:col-span-2 flex items-center gap-3">
            <input
              type="checkbox"
              id="spotlight-enabled"
              checked={spotlight.enabled}
              onChange={(e) => setSpotlight(prev => ({ ...prev, enabled: e.target.checked }))}
              className="w-4 h-4 rounded cursor-pointer"
            />
            <label htmlFor="spotlight-enabled" className="text-sm text-white cursor-pointer">
              ✓ Enable this spotlight placement
            </label>
          </div>
        </div>
        {spotlight.tool_id && spotlight.enabled && (
          <div className="mt-4 p-3 rounded" style={{ background: 'rgba(255,232,107,0.08)', border: '1px solid rgba(255,232,107,0.2)' }}>
            <p className="text-sm text-white">
              ✓ <strong>{allTools.find(t => t.id === spotlight.tool_id || t.slug === spotlight.tool_id)?.name || 'Tool'}</strong> will appear in homepage hero from <strong>{spotlight.start_date}</strong> to <strong>{spotlight.end_date}</strong> for <strong>${spotlight.price}</strong>
            </p>
          </div>
        )}
      </div>

      {/* Legacy: Sponsored Placements */}
      <div className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6">
        <div className={card}>
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Sponsored Placements</h3>
                <InfoTag label="Manage paid sponsorships" description="Create and manage sponsored tool placements with time-limited promotions and tracking" variant="success" />
              </div>
              <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Manage monetized placements separately from recommendation rankings.</p>
            </div>
            <SaveBar saving={saving} onSave={save} label="Save Sponsorship" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextInput label="Sponsor Name" value={form.sponsor_name} onChange={(value) => setForm((prev) => ({ ...prev, sponsor_name: value }))} />
            <TextInput label="Slot" value={form.slot} onChange={(value) => setForm((prev) => ({ ...prev, slot: value }))} />
            <TextInput label="Target URL" value={form.target_url} onChange={(value) => setForm((prev) => ({ ...prev, target_url: value }))} />
            <TextInput label="Badge Label" value={form.badge_label} onChange={(value) => setForm((prev) => ({ ...prev, badge_label: value }))} />
            <SelectInput label="Status" value={form.status} onChange={(value) => setForm((prev) => ({ ...prev, status: value }))} options={[{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }, { value: 'draft', label: 'Draft' }]} />
            <TextInput label="Start Date" value={form.start_date} onChange={(value) => setForm((prev) => ({ ...prev, start_date: value }))} />
            <TextInput label="End Date" value={form.end_date} onChange={(value) => setForm((prev) => ({ ...prev, end_date: value }))} />
            <div className="md:col-span-2">
              <TextArea label="Notes" value={form.notes} onChange={(value) => setForm((prev) => ({ ...prev, notes: value }))} rows={5} />
            </div>
          </div>
        </div>
        <ListCard
          title="Active Placements"
          items={items}
          emptyMessage={loading ? 'Loading placements...' : 'No sponsorships yet.'}
          renderItem={(item) => (
            <div className="flex items-start justify-between gap-4">
              <div>
              <p className="text-white font-semibold">{item.sponsor_name}</p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.slot} - {item.target_url}</p>
            </div>
            <StatusPill status={item.status} />
          </div>
        )}
      />
      </div>
    </motion.div>
  );
};

export const GuidesAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ id: '', title: '', slug: '', excerpt: '', body: '', category: 'buyers-guide', status: 'draft', cover_image: '', related_tool_slugs: '', seo_keyword: '' });

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getGuidesAdmin();
      setItems(response.data?.items || []);
    } catch {
      alert('Failed to load guides');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setForm({ id: '', title: '', slug: '', excerpt: '', body: '', category: 'buyers-guide', status: 'draft', cover_image: '', related_tool_slugs: '', seo_keyword: '' });
  };

  const editGuide = (guide) => {
    setForm({
      id: guide.id || '',
      title: guide.title || '',
      slug: guide.slug || '',
      excerpt: guide.excerpt || '',
      body: guide.body || '',
      category: guide.category || 'buyers-guide',
      status: guide.status || 'draft',
      cover_image: guide.cover_image || '',
      related_tool_slugs: Array.isArray(guide.related_tool_slugs) ? guide.related_tool_slugs.join(', ') : '',
      seo_keyword: guide.seo_keyword || '',
    });
  };

  const deleteGuide = async (guideId) => {
    if (!window.confirm('Delete this guide?')) return;
    setSaving(true);
    try {
      await adminApi.deleteGuideAdmin(guideId);
      if (form.id === guideId) {
        resetForm();
      }
      await load();
    } catch {
      alert('Failed to delete guide');
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveGuideAdmin({ ...form, related_tool_slugs: splitCsv(form.related_tool_slugs) });
      resetForm();
      await load();
    } catch {
      alert('Failed to save guide');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...sectionMotion} className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6" data-testid="guides-admin-tab">
      <div className={card}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Guide Publisher</h3>
              <InfoTag label="Publish educational guides" description="Create buyers guides, implementation playbooks, and consulting resources to support customer decisions" variant="primary" />
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Publish buyer guides and rollout playbooks that support consulting and conversion flows.</p>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={resetForm} disabled={saving}>New Guide</button>
            <SaveBar saving={saving} onSave={save} label={form.id ? 'Update Guide' : 'Save Guide'} />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Title" value={form.title} onChange={(value) => setForm((prev) => ({ ...prev, title: value }))} />
          <TextInput label="Slug" value={form.slug} onChange={(value) => setForm((prev) => ({ ...prev, slug: value }))} />
          <SelectInput label="Category" value={form.category} onChange={(value) => setForm((prev) => ({ ...prev, category: value }))} options={[{ value: 'buyers-guide', label: 'Buyers Guide' }, { value: 'implementation', label: 'Implementation' }, { value: 'consulting', label: 'Consulting' }, { value: 'playbook', label: 'Playbook' }]} />
          <SelectInput label="Status" value={form.status} onChange={(value) => setForm((prev) => ({ ...prev, status: value }))} options={[{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }]} />
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Cover Image
            <div className="mt-2 flex gap-2 items-center">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      const response = await adminApi.uploadGuideImage(file);
                      setForm((prev) => ({ ...prev, cover_image: response.data.url }));
                    } catch (error) {
                      alert('Failed to upload image: ' + (error?.response?.data?.detail || 'Unknown error'));
                    }
                  }
                }}
                className="bg-transparent border border-white/20 text-white px-2 py-1 rounded text-xs cursor-pointer"
              />
              {form.cover_image && (
                <span style={{ color: 'var(--accent-primary)', fontSize: '0.75rem' }}>✓ Uploaded</span>
              )}
            </div>
            {form.cover_image && (
              <div className="mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                URL: {form.cover_image}
              </div>
            )}
          </label>
          <TextInput label="SEO Keyword" value={form.seo_keyword} onChange={(value) => setForm((prev) => ({ ...prev, seo_keyword: value }))} />
          <div className="md:col-span-2">
            <TextInput label="Related Tool Slugs" value={form.related_tool_slugs} onChange={(value) => setForm((prev) => ({ ...prev, related_tool_slugs: value }))} placeholder="claude, chatgpt" />
          </div>
          <div className="md:col-span-2">
            <TextArea label="Excerpt" value={form.excerpt} onChange={(value) => setForm((prev) => ({ ...prev, excerpt: value }))} rows={3} />
          </div>
          <div className="md:col-span-2">
            <TextArea label="Body" value={form.body} onChange={(value) => setForm((prev) => ({ ...prev, body: value }))} rows={10} />
          </div>
        </div>
      </div>
      <ListCard
        title="Published and Draft Guides"
        items={items}
        emptyMessage={loading ? 'Loading guides...' : 'No guides yet.'}
        renderItem={(item) => (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-semibold">{item.title}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>/guides/{item.slug}</p>
              </div>
              <StatusPill status={item.status} />
            </div>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.excerpt || 'No excerpt provided.'}</p>
            <div className="flex gap-2 flex-wrap">
              <button className="btn-secondary text-xs" onClick={() => editGuide(item)}>Edit</button>
              <button className="btn-danger text-xs" onClick={() => deleteGuide(item.id)}>Delete</button>
            </div>
          </div>
        )}
      />
    </motion.div>
  );
};

export const PartnersAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: '', partner_type: 'affiliate', status: 'active', website_url: '', contact_email: '', oauth_enabled: false, client_access_enabled: false, notes: '', onboarding_stage: 'identified' });

  const load = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getPartnersAdmin();
      setItems(response.data?.items || []);
    } catch {
      alert('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.savePartnerAdmin(form);
      setForm({ name: '', partner_type: 'affiliate', status: 'active', website_url: '', contact_email: '', oauth_enabled: false, client_access_enabled: false, notes: '', onboarding_stage: 'identified' });
      await load();
    } catch {
      alert('Failed to save partner');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...sectionMotion} className="grid grid-cols-1 xl:grid-cols-[0.95fr_1.05fr] gap-6" data-testid="partners-admin-tab">
      <div className={card}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Partner Ops</h3>
              <InfoTag label="Manage partner relationships" description="Track affiliate, technology, and agency partnership onboarding, OAuth integration, and client access" variant="success" />
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Track partner onboarding, OAuth readiness, and client-access alignment.</p>
          </div>
          <SaveBar saving={saving} onSave={save} label="Save Partner" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextInput label="Partner Name" value={form.name} onChange={(value) => setForm((prev) => ({ ...prev, name: value }))} />
          <SelectInput label="Type" value={form.partner_type} onChange={(value) => setForm((prev) => ({ ...prev, partner_type: value }))} options={[{ value: 'affiliate', label: 'Affiliate' }, { value: 'technology', label: 'Technology' }, { value: 'agency', label: 'Agency' }, { value: 'consulting', label: 'Consulting' }]} />
          <SelectInput label="Status" value={form.status} onChange={(value) => setForm((prev) => ({ ...prev, status: value }))} options={[{ value: 'active', label: 'Active' }, { value: 'paused', label: 'Paused' }, { value: 'prospect', label: 'Prospect' }]} />
          <TextInput label="Website URL" value={form.website_url} onChange={(value) => setForm((prev) => ({ ...prev, website_url: value }))} />
          <TextInput label="Contact Email" value={form.contact_email} onChange={(value) => setForm((prev) => ({ ...prev, contact_email: value }))} />
          <TextInput label="Onboarding Stage" value={form.onboarding_stage} onChange={(value) => setForm((prev) => ({ ...prev, onboarding_stage: value }))} />
          <CheckboxInput label="OAuth enabled" checked={!!form.oauth_enabled} onChange={(value) => setForm((prev) => ({ ...prev, oauth_enabled: value }))} />
          <CheckboxInput label="Client access enabled" checked={!!form.client_access_enabled} onChange={(value) => setForm((prev) => ({ ...prev, client_access_enabled: value }))} />
          <div className="md:col-span-2">
            <TextArea label="Notes" value={form.notes} onChange={(value) => setForm((prev) => ({ ...prev, notes: value }))} rows={6} />
          </div>
        </div>
      </div>
      <ListCard
        title="Partner Pipeline"
        items={items}
        emptyMessage={loading ? 'Loading partners...' : 'No partner records yet.'}
        renderItem={(item) => (
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-white font-semibold">{item.name}</p>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.partner_type} - {item.contact_email || 'No contact email'}</p>
              </div>
              <StatusPill status={item.status} />
            </div>
            <div className="flex gap-2 flex-wrap text-xs">
              <span style={{ color: 'var(--text-muted)' }}>OAuth: {item.oauth_enabled ? 'yes' : 'no'}</span>
              <span style={{ color: 'var(--text-muted)' }}>Client access: {item.client_access_enabled ? 'yes' : 'no'}</span>
              <span style={{ color: 'var(--text-muted)' }}>Stage: {item.onboarding_stage || 'identified'}</span>
            </div>
          </div>
        )}
      />
    </motion.div>
  );
};

export const SeoSettingsAdminTab = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    canonical_host: 'https://wowrolla.ca',
    faq_schema_enabled: true,
    changelog_schema_enabled: true,
    guide_schema_enabled: true,
    sitemap_guides_enabled: true,
    sitemap_changelog_enabled: true,
    noindex_drafts: true,
    auto_internal_links: true,
    llms_txt_enabled: true,
    answer_snippet_blocks_enabled: true,
    entity_coverage_enforced: true,
    min_source_citations_per_page: 2,
    robots_overrides: {},
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await adminApi.getSeoSettings();
        setSettings((prev) => ({ ...prev, ...(response.data || {}) }));
      } catch {
        alert('Failed to load SEO settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateSeoSettings(settings);
    } catch {
      alert('Failed to save SEO settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div {...sectionMotion} data-testid="seo-settings-admin-tab">
      <div className={card}>
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Global SEO Defaults</h3>
              <InfoTag label="Configure SEO standards" description="Control schema markup, sitemap inclusion, indexation rules, and entity coverage across all content" variant="primary" />
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>Control schema, sitemap inclusion, and draft indexation across all generated content.</p>
          </div>
          <SaveBar saving={saving} onSave={save} label="Save SEO Defaults" />
        </div>
        {loading ? (
          <p style={{ color: 'var(--text-secondary)' }}>Loading SEO settings...</p>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="Canonical Host" value={settings.canonical_host} onChange={(value) => setSettings((prev) => ({ ...prev, canonical_host: value }))} />
              <TextInput label="Minimum source citations per page" value={String(settings.min_source_citations_per_page ?? 2)} onChange={(value) => setSettings((prev) => ({ ...prev, min_source_citations_per_page: Number(value || 0) }))} />
              <CheckboxInput label="Auto internal links" checked={!!settings.auto_internal_links} onChange={(value) => setSettings((prev) => ({ ...prev, auto_internal_links: value }))} />
              <CheckboxInput label="FAQ schema enabled" checked={!!settings.faq_schema_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, faq_schema_enabled: value }))} />
              <CheckboxInput label="Changelog schema enabled" checked={!!settings.changelog_schema_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, changelog_schema_enabled: value }))} />
              <CheckboxInput label="Guide schema enabled" checked={!!settings.guide_schema_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, guide_schema_enabled: value }))} />
              <CheckboxInput label="Include guides in sitemap" checked={!!settings.sitemap_guides_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, sitemap_guides_enabled: value }))} />
              <CheckboxInput label="Include changelogs in sitemap" checked={!!settings.sitemap_changelog_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, sitemap_changelog_enabled: value }))} />
              <CheckboxInput label="Noindex draft pages" checked={!!settings.noindex_drafts} onChange={(value) => setSettings((prev) => ({ ...prev, noindex_drafts: value }))} />
              <CheckboxInput label="Enable llms.txt strategy" checked={!!settings.llms_txt_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, llms_txt_enabled: value }))} />
              <CheckboxInput label="Require answer-first snippet blocks" checked={!!settings.answer_snippet_blocks_enabled} onChange={(value) => setSettings((prev) => ({ ...prev, answer_snippet_blocks_enabled: value }))} />
              <CheckboxInput label="Enforce entity coverage targets" checked={!!settings.entity_coverage_enforced} onChange={(value) => setSettings((prev) => ({ ...prev, entity_coverage_enforced: value }))} />
            </div>
            <div className="mt-4 p-4" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
              <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>SEO + AEO best-practice pointers</p>
              <ul className="text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                <li>- Keep one direct answer block in the first viewport for AI answer engines.</li>
                <li>- Use verifiable source links and fresh stats to raise citation confidence.</li>
                <li>- Structure key sections with question-led headings that mirror user prompts.</li>
                <li>- Pair narrative copy with comparison tables and schema for extraction-friendly layout.</li>
              </ul>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

const MetricCard = ({ label, value }) => (
  <div className={card}>
    <p className="text-xs uppercase font-mono mb-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
    <p className="text-2xl font-bold" style={{ color: 'var(--accent-primary)' }}>{value}</p>
  </div>
);

const ListCard = ({ title, items, renderItem, emptyMessage = 'No items yet.' }) => (
  <div className={card}>
    <h3 className="text-xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>{title}</h3>
    {items.length === 0 ? (
      <p style={{ color: 'var(--text-secondary)' }}>{emptyMessage}</p>
    ) : (
      <div className="space-y-3 max-h-[42rem] overflow-y-auto pr-1">
        {items.map((item, index) => (
          <div key={item.id || item.slug || item.tool_id || index} className="p-4" style={shellStyle}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    )}
  </div>
);

